/* eslint-disable react/sort-comp,react/forbid-prop-types */
import classNames from 'classnames';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  result,
  chain,
  reduce,
  extend,
  max,
  throttle,
  map,
  contains,
  keys,
  isEmpty,
  isString,
} from 'underscore';
import $ from 'jquery';
import { ResizeObserver as ResizeObserverPollyFill } from '@juggle/resize-observer';

import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { DisclaimerBanner } from '@bingads-webui-campaign-react/help';
import { ContentFooter } from '@bingads-webui-campaign-react/content-footer';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getBidLandscapeStyle } from '@bingads-webui-campaign/bid-landscape-style';
import { getBudgetSuggestionPopupStyle } from '@bingads-webui-campaign/budget-suggestion-popup-style';
import { getInlineOpportunityStyle } from '@bingads-webui-campaign/inline-opportunity-style';
import { helpbuttonStyle, uiblockerStyle } from '@bingads-webui-campaign-react/common-styles';
import { gridPaddingLeft } from '@bingads-webui-campaign-react/grid-view-wrapper';
import { getHeightIncludeAbsolutePositionedElements } from '@bingads-webui-campaign-react/virtual-scrollbar-recalculate-trigger';
import { Scrollbar } from './scrollbar';

const SCROLLBAR_WIDTH = 17;

const getShadowWidthOutofYBorder = ($el) => {
  const boxShadow = $el.css('box-shadow');
  if (boxShadow === 'none') {
    return 0;
  }

  const pixels = map(boxShadow.match(/([+-.0-9]+)(?=px)/g), v => Number(v));
  // only handle one shadow
  // offset-y + blur radius + spread radius
  return pixels[1] + pixels[2] + pixels[3];
};

const getTableView = (props) => {
  const p$gridViewContainer = props.backboneGridView || props.pgridView;
  const tableView = result(p$gridViewContainer, '_tableView') ||
    chain(p$gridViewContainer)
      .result('grid')
      .result('gridView')
      .result('_tableView')
      .value() ||
    chain(p$gridViewContainer)
      .result('grid')
      .result('grid')
      .result('gridView')
      .result('_tableView')
      .value();

  return tableView;
};

const getTableWidth = ($table) => {
  const tableWidth = reduce(
    $table.find('.column-header').toArray(),
    (memo, i) => memo + i.getBoundingClientRect().width,
    0
  );
  return tableWidth;
};

const getTablePageStyles = (_, { palette = {} }) => extend(
  {
    pageContainer: {
      display: 'flex',
      flex: '1 0 1px',
      flexDirection: 'column',
      overflow: 'auto',
    },
    gridContainer: {
      overflowX: 'hidden',
      overflowY: 'hidden',
    },
    gridViewContainer: {
      marginRight: 15,
    },
    contentFooter: {
      display: 'flex',
      flexDirection: 'column-reverse',
      width: '100%',
      position: 'static',
      marginTop: 'auto',
    },
    resizeMarker: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      zIndex: '-1',
      visibility: 'hidden',
    },
  },
  {
    '@global': extend(
      uiblockerStyle({ palette }),
      getBidLandscapeStyle(palette),
      getBudgetSuggestionPopupStyle(palette),
      getInlineOpportunityStyle(palette),
      helpbuttonStyle({ palette }),
      {
        // [TODO] Move to theme-fluent/src/components/grid-shared/global.js
        '.grid-noresult, .grid-noresult-empty, .noresult': {
          display: 'none',
        },
        '.virtual-scrollbar-container': {
          '& .parent-bar': {
            backgroundColor: '#f1f1f1',
          },
          '& .child-bar': {
            backgroundColor: '#c1c1c1',
            '&:hover': {
              backgroundColor: '#a8a8a8',
            },
          },
        },
      }
    ),
  }
);

class TablePageContainer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      gridPopupHeight: -1,
      gridViewWidth: -1,

      // In case of no IntersectionObserver support on browser, use this
      // default virtualscrollbar state, position: fixed, bottom: 0
      virtualScrollbarFixed: true,
      virtualScrollbarInView: true,

      containerOverflowX: false,
    };

    this.gridEventMap = {};
    this.lastVirtualScrollLeft = 0;
    this.letPopUpScrollHorizontal = false;

    this.viewport = React.createRef();
    this.gridAndStickyContainer = React.createRef();
    this.topOfGridContainer = React.createRef();
    this.stickyContainer = React.createRef();
    this.gridAndDisclaimContainer = React.createRef();

    // Use the feature of 'IntersectionObserver' to update the horizontal virtual scrollbar
    this.topMarker = { el: null, isInView: null };
    this.bottomMarker = { el: null, isInView: null, isHiddenInView: null };

    if (window && 'IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.handleIntersectionChange.bind(this), {
        root: null,
        rootMargin: '0px',
        threshold: [0, 1],
      });
    } else {
      // All supported browsers are providing this feature, this is a fallback for backword compatibility
      this.intersectionObserver = null;
    }

    this.resizeMarker = React.createRef();
    this.lastResizerWidth = 0;
    if (window && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(throttle(this.handleResizeChange.bind(this), 250));
    } else {
      // All supported browsers are providing this feature, if not, switch to polly fill
      this.resizeObserver = new ResizeObserverPollyFill(throttle(this.handleResizeChange.bind(this), 250));
    }

    const SMALL_PADDING_LEFT_CLASS = 'small-padding-left';
    let shouldUseSmallPadding = false;
    if (isString(this.props.customClasses.tablePageContainer)) {
      shouldUseSmallPadding = this.props.customClasses.tablePageContainer.includes(SMALL_PADDING_LEFT_CLASS);
    } else {
      shouldUseSmallPadding = contains(this.props.customClasses.tablePageContainer, SMALL_PADDING_LEFT_CLASS);
    }

    this.paddingLeft = shouldUseSmallPadding ? 0 : gridPaddingLeft;
  }

  getChildBarScrollLeftValue() {
    if (this.state.gridViewWidth === -1) {
      return 0;
    }
    const childBar = $('.child-bar');
    // align with scrollbar;
    const viewWidth = this.state.gridViewWidth + this.paddingLeft + SCROLLBAR_WIDTH;

    if (childBar) {
      const parentWidth = this.state.stickyTopOfGridWidth;
      const childOffset = childBar.position().left;
      return (childOffset / parentWidth) * viewWidth;
    }
    return 0;
  }

  handleResizeChange(entries) {
    // For now, we only observe one entry and we only compare one threshold.
    const entry = entries[0];
    const threshold = this.props.tablePageMinWidth;

    // Decide whether to adjust virtual scrollbar
    const { width } = entry.contentRect;
    if ((this.lastResizerWidth === 0 || this.lastResizerWidth >= threshold) && width < threshold) {
      this.setState({
        containerOverflowX: true,
      });
    }

    if (this.lastResizerWidth < threshold && width >= threshold) {
      this.setState({
        containerOverflowX: false,
      });
    }

    this.lastResizerWidth = width;

    // notify gridView
    // FIXME: the below logic is also used by inline chart, we should have other ways to trigger the grid re-render
    const p$gridViewContainer = this.props.backboneGridView || this.props.pgridView;
    if (p$gridViewContainer) {
      const tableView = getTableView(this.props);
      if (tableView) {
        tableView.trigger('didChangeBound'); // eslint-disable-line no-underscore-dangle
      }
    }

    if (this.viewport && this.viewport.current) {
      let event;
      const eventName = 'scroll';
      if (typeof Event === 'function') {
        event = new Event(eventName);
      } else {
        // IE 11 patch
        event = document.createEvent('Event');
        event.initEvent(eventName, false, false);
      }

      this.viewport.current.dispatchEvent(event);
    }
  }

  handleIntersectionChange(entries) {
    let handleScrollbar = false;

    entries.forEach((entry) => {
      if (entry.target && entry.target === this.topMarker.el) {
        // Sometimes the top marker (the thead) has zero intersection height, which will cause the intersectionRation to be 0
        this.topMarker.isInView = entry.isIntersecting; // (entry.intersectionRatio !== 0);

        handleScrollbar = true;
      }
      if (entry.target && entry.target === this.bottomMarker.el) {
        this.bottomMarker.isInView = (entry.intersectionRatio !== 0);
        this.bottomMarker.isHiddenInView = false;

        handleScrollbar = true;
      }
    });

    if (handleScrollbar) {
      this.handleScrollbar();
    }
  }

  handleScrollbar() {
    if (this.bottomMarker.isInView) {
      // Append the scrollbar at the end of the table
      this.setState({
        virtualScrollbarFixed: false,
        virtualScrollbarInView: true,
      });
    } else if (this.topMarker.isInView) {
      // Detach the scrollbar and make it fixed, with bottom: 0
      this.setState({
        virtualScrollbarFixed: true,
        virtualScrollbarInView: true,
      });
    } else {
      // The grid is completely pushed out of viewport by top of grid panel, hide scrollbar.
      // Note: it is also possible that the bottom marker element is hidden (display: none)
      //       we will get a chance to evaluate that again before rendering.
      this.setState({
        virtualScrollbarInView: false,
      });
    }
  }

  handlePopup(prevPopupHeight) {
    const popupHeight = this.getInlineEditPopupHeight();
    if (!prevPopupHeight || popupHeight !== prevPopupHeight) {
      this.setState({ gridPopupHeight: popupHeight });
    }
    if (this.letPopUpScrollHorizontal) {
      this.onScrollbarsViewUpdate();
    }
  }

  handleScroll(prevState = {}) {
    const p$gridViewContainer = this.props.backboneGridView || this.props.pgridView;
    if (p$gridViewContainer) {
      // when parentElement is null, grid view is not in dom yet.
      // Calling getBoundingClientRect will cause unspecified error in IE.
      // no need to handle scroll at this moment.
      if (p$gridViewContainer.$el.get(0).parentElement === null) {
        return;
      }

      const viewportRect = this.viewport.current.getBoundingClientRect();
      const topOfGridRect = this.topOfGridContainer.current.getBoundingClientRect();
      const gridAndStickyRect = this.gridAndStickyContainer.current.getBoundingClientRect();
      const stickyContainer = this.stickyContainer.current.getBoundingClientRect();

      // we have two different modes for positioning sticky top of grid content depending on scroll position
      // check here if the sticky top of grid content would have already been visible in the viewport without
      // sticky behavior (just based on element placement)
      const shouldAdjustStickyTopOfGrid = gridAndStickyRect.top < viewportRect.top;


      // only display virtual scrollbar if grid has horizontal overflow and...
      const tableWidth = getTableWidth(p$gridViewContainer.$('table')); // Sovle the Mac Safari issue
      const gridViewHasHorizontalOverflow = Math.round(tableWidth) > Math.round(viewportRect.width) - this.paddingLeft;
      let emptyHeight = Math.round(viewportRect.height) - Math.round(gridAndStickyRect.height) - Math.round(topOfGridRect.height);
      emptyHeight = shouldAdjustStickyTopOfGrid ? emptyHeight + stickyContainer.height : emptyHeight;
      const gridViewHasVerticalOverflow = emptyHeight < 0;

      const newState = {
        stickyTopOfGridTopOffset: shouldAdjustStickyTopOfGrid ? viewportRect.top : 0,
        stickyTopOfGridPosition: shouldAdjustStickyTopOfGrid ? 'fixed' : 'relative',
        stickyTopOfGridWidth: Math.floor(viewportRect.width - (gridViewHasVerticalOverflow ? SCROLLBAR_WIDTH : 0)),
        stickyTopOfGridHeight: stickyContainer.height,
        gridViewHasHorizontalOverflow,
        gridViewHasVerticalOverflow,
        shouldAdjustStickyTopOfGrid,
      };

      const scrollStateKeys = keys(newState);

      const modifiedState = reduce(scrollStateKeys, (memo, stateKey) => {
        if (prevState[stateKey] !== newState[stateKey]) {
          memo[stateKey] = newState[stateKey]; // eslint-disable-line no-param-reassign
        }

        return memo;
      }, {});

      if (!isEmpty(modifiedState)) {
        this.setState(modifiedState);
      }
    }

    // Also adjust the grid+disclaimer height based on popupheight.
    this.handlePopup(prevState.gridPopupHeight);
  }

  componentDidMount() {
    if (this.resizeObserver && this.resizeMarker && this.resizeMarker.current) {
      this.resizeObserver.observe(this.resizeMarker.current);
    }
    this.setScrollPosition();
    this.handleScroll();
    $(this.viewport.current).on('resizeContainerBasedOnPopupHeight', (event, options) => {
      // Assume here is the reset,
      this.letPopUpScrollHorizontal = options ? options.letPopUpScrollHorizontal : false;
      if (this.letPopUpScrollHorizontal) {
        this.lastVirtualScrollLeft = this.getChildBarScrollLeftValue();
      }
      this.handlePopup();
    });
  }

  componentWillUnmount() {
    const p$gridView = (this.props.backboneGridView && this.props.backboneGridView.grid && this.props.backboneGridView.grid.gridView) || this.props.pgridView;
    if (p$gridView) {
      p$gridView.off('didUpdate');
    }

    if (this.intersectionObserver) {
      if (this.topMarker.el) {
        this.intersectionObserver.unobserve(this.topMarker.el);
      }

      if (this.bottomMarker.el) {
        this.intersectionObserver.unobserve(this.bottomMarker.el);
      }

      this.intersectionObserver = null;
    }

    if (this.resizeObserver && this.resizeMarker && this.resizeMarker.current) {
      this.resizeObserver.unobserve(this.resizeMarker.current);
    }

    $(this.viewport.current).off('resizeContainerBasedOnPopupHeight');
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.stickyTopOfGridPosition === 'fixed' && prevState.stickyTopOfGridPosition !== 'fixed') {
      const stickyContainer = this.stickyContainer.current.getBoundingClientRect();
      this.shiftThePopUp(stickyContainer.height);
    } else if (this.state.stickyTopOfGridPosition !== 'fixed' && prevState.stickyTopOfGridPosition === 'fixed') {
      const stickyContainer = this.stickyContainer.current.getBoundingClientRect();
      this.shiftThePopUp(-stickyContainer.height);
    }
    this.setScrollPosition(prevProps);
    this.handleScroll(prevState);
  }

  onScrollbarsViewUpdate() {
    const $popup = $(this.props.inlineEditPopupSelector);
    const currentLeft = this.getChildBarScrollLeftValue();
    if ($popup.length) {
      const cssLeft = parseFloat($popup.css('left'));
      const newLeft = cssLeft - (currentLeft - this.lastVirtualScrollLeft);
      $popup.css('left', `${newLeft}px`);
    }
    this.lastVirtualScrollLeft = currentLeft;
  }

  shiftThePopUp(height) {
    let $popup = $(this.props.inlineEditPopupSelector);
    if (!$popup.attr('style')) {
      $popup = $(this.props.inlineEditPopupSelector).closest('[style*="absolute"]');
    }
    if ($popup.length) {
      const cssTop = parseFloat($popup.css('top'));
      const newTop = cssTop + height;
      $popup.css('top', `${newTop}px`);
    }
  }

  setScrollPosition(prevProps) {
    if (this.props.forceResetScrollFlag !== (prevProps && prevProps.forceResetScrollFlag)) {
      this.viewport.current.scrollTop = 0;
    }
  }

  getInlineEditPopupHeight() {
    // TODO: should also check filter editor popup
    const popup = document.querySelector(this.props.inlineEditPopupSelector);
    const popupHeight = getHeightIncludeAbsolutePositionedElements(popup);

    if (popupHeight) {
      const popupTop = $(popup).offset().top - $('.grid-and-disclaim-container').offset().top;
      return popupTop + popupHeight + getShadowWidthOutofYBorder($(popup));
    }

    return -1;
  }

  render() {
    const p$gridView = (this.props.backboneGridView && this.props.backboneGridView.grid && this.props.backboneGridView.grid.gridView) || this.props.pgridView;

    if (p$gridView && this.intersectionObserver) {
      // Find a chance to initiate the intersection observing
      const thead = p$gridView.$('thead').get(0);
      if (thead && thead !== this.topMarker.el) {
        if (this.topMarker.el) {
          this.intersectionObserver.unobserve(this.topMarker.el);
        }
        this.topMarker.el = thead;
        this.intersectionObserver.observe(this.topMarker.el);
      }

      const tfoot = p$gridView.$('tfoot').get(0);
      if (tfoot && tfoot !== this.bottomMarker.el) {
        if (this.bottomMarker.el) {
          this.intersectionObserver.unobserve(this.bottomMarker.el);
        }
        this.bottomMarker.el = tfoot;
        this.intersectionObserver.observe(this.bottomMarker.el);
      }
    }

    if (p$gridView && !this.gridEventMap[p$gridView.cid]) {
      p$gridView.on('didUpdate', () => {
        const tableWidth = getTableWidth(p$gridView.$el.find('table'));
        // // only for safari. The root cause is we should set a width for the fixed-layout table
        // window.safari && p$gridView.$el.find('table').css('min-width', tableWidth);

        this.setState({
          // get notified when grid width changed by column-resize plugin
          gridViewWidth: tableWidth,
        });
      });

      this.gridEventMap[p$gridView.cid] = true;
    }

    const CustomDisclaimerBanner = this.props.CustomDisclaimerBanner || DisclaimerBanner;

    const stickyStyle = { position: this.state.stickyTopOfGridPosition, top: this.state.stickyTopOfGridTopOffset, zIndex: Constants.ZIndexContentComponents.GridToolbar + 1 };

    // Do the final adjustment before rendering the virtual scrollbar
    let { virtualScrollbarFixed, virtualScrollbarInView } = this.state;

    if (this.state.shouldAdjustStickyTopOfGrid) {
      stickyStyle.width = this.state.stickyTopOfGridWidth;
    }

    if (this.intersectionObserver) {
      if (!this.bottomMarker.isInView && this.bottomMarker.el) {
        // By the time when we handle intersection change, the client rect is not always updated
        const bottomMarkerRect = this.bottomMarker.el.getBoundingClientRect();
        if (bottomMarkerRect.top === 0 && bottomMarkerRect.height === 0) {
          // The 'tfoot' is hidden, no-result is shown
          this.bottomMarker.isHiddenInView = true;

          virtualScrollbarFixed = false;
          virtualScrollbarInView = true;
        }
      }
    }

    const gridAndDisclaimContainerHeight = this.gridAndDisclaimContainer.current ? this.gridAndDisclaimContainer.current.getBoundingClientRect().height : 0;

    return (
      <Fragment>
        <div ref={this.resizeMarker} className={classNames([this.props.classes.resizeMarker])} />
        <div
          ref={this.viewport}
          className={classNames([this.props.customClasses.tablePageContainer, this.props.classes.pageContainer, 'table-page-container'])}
          onScroll={() => this.handleScroll()}
          style={{ minWidth: this.props.tablePageMinWidth }}
        >
          <div ref={this.topOfGridContainer}>
            {this.props.topOfGridContent}
          </div>
          <div ref={this.gridAndStickyContainer} className="grid-and-sticky-container" style={{ position: 'relative', zIndex: 0 }}>
            {/* Scrollbar container (of whom grid view is a descendant) is position:relative to allow offseting the virtual scrollbars.
          Normal stacking order causes elements who are position:relative to be stacked in the order in which they appear in the DOM, so toolbar will always be
          hidden behind the scrollbar container. Setting a z-index here to prevent scrollbar container (i.e. the grid view) from occluding the sticky toolbar */}
            <div ref={this.stickyContainer} style={stickyStyle} className="sticky-container">
              {this.props.stickyTopOfGridContent}
            </div>
            <div ref={this.gridAndDisclaimContainer} className={classNames([this.props.classes.gridAndDisclaimContainer, 'grid-and-disclaim-container'])} style={{ height: this.state.gridPopupHeight < 0 ? 'unset' : max([this.state.gridPopupHeight, gridAndDisclaimContainerHeight]), position: 'relative' }}>
              <div className={classNames([this.props.classes.gridContainer])}>
                <div className={classNames([this.props.customClasses.gridViewContainer, 'bingadsgrid', this.props.classes.gridViewContainer])} style={{ width: this.state.stickyTopOfGridWidth, position: 'relative', marginTop: this.state.shouldAdjustStickyTopOfGrid ? `${this.state.stickyTopOfGridHeight}px` : '0px' }}>
                  <div className="budget-lanscape-popover-container" />
                  <div className="performance-insights-popover-container" />
                  <Scrollbar
                    pgridView={p$gridView}
                    width={this.state.stickyTopOfGridWidth}
                    shouldDisplay={virtualScrollbarInView && this.state.gridViewHasHorizontalOverflow}
                    positionFixed={virtualScrollbarFixed}
                    containerOverflowX={this.state.containerOverflowX}
                    gridViewWidth={this.state.gridViewWidth}
                    gridPaddingLeft={this.paddingLeft}
                    noResultSelector="grid-noresult"
                  >
                    <div className="grid-parent-container">
                      <div id="rejectionContainer" />
                      {this.props.gridView}
                    </div>
                  </Scrollbar>
                  <div className="grid-noresult" />
                </div>
              </div>
              {
                // customer-scope grids don't have timeZone info
                // Disclaimer banner will show if either timeZone or CustomDisclaimerBanner valid in props
                (this.props.timeZone || this.props.CustomDisclaimerBanner) && (
                  <div>
                    <CustomDisclaimerBanner timezone={this.props.timeZone} />
                  </div>
                )
              }
            </div>
          </div>
          {!this.props.disableFooter &&
            <div className={this.props.classes.contentFooter}>
              <ContentFooter />
            </div>
          }
        </div>
      </Fragment >
    );
  }
}

TablePageContainer.propTypes = {
  topOfGridContent: PropTypes.node,
  stickyTopOfGridContent: PropTypes.node,
  classes: PropTypes.object.isRequired,
  customClasses: PropTypes.object,
  timeZone: PropTypes.number,
  gridView: PropTypes.object.isRequired,
  backboneGridView: PropTypes.object,
  pgridView: PropTypes.object,
  forceResetScrollFlag: PropTypes.bool,
  tablePageMinWidth: PropTypes.number,
  disableFooter: PropTypes.bool,
  CustomDisclaimerBanner: PropTypes.elementType,
  inlineEditPopupSelector: PropTypes.string,
};

TablePageContainer.defaultProps = {
  customClasses: {},
  topOfGridContent: null,
  backboneGridView: null,
  pgridView: null,
  forceResetScrollFlag: false,
  tablePageMinWidth: 1032,
  timeZone: null,
  disableFooter: false,
  CustomDisclaimerBanner: null,
  inlineEditPopupSelector: '.grid-and-disclaim-container [role=dialog]',
  stickyTopOfGridContent: null,
};


export const StyledTablePageContainer = withDefaultStyles(TablePageContainer, getTablePageStyles);
